import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { SortKey } from "@/types/anime";
import { formatDate, formatViews, getPopular } from "@/utils/anime";

const PAGE_SIZE = 12;

const tabOptions: { key: SortKey; label: string }[] = [
  { key: "popular", label: "按浏览热度" },
  { key: "updated", label: "按最近更新" },
  { key: "alpha", label: "按字母排序" },
];

export default function PopularPage() {
  const list = useMemo(() => getPopular(40), []);
  const [sort, setSort] = useState<SortKey>("popular");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: "热门推荐 - 色情动漫高热度作品榜单",
    description: "色情动漫热门推荐页，依据站内浏览与收藏数据模拟热度，整理成人向动画作品排行榜，附类型、年份与更新状态信息。",
    keywords: "色情动漫,热门动漫,动漫推荐,动漫排行,成人动漫,高人气动漫",
  });

  const sorted = useMemo(() => {
    const next = [...list];
    if (sort === "alpha") {
      next.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    } else if (sort === "updated") {
      next.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    } else {
      next.sort((a, b) => b.views - a.views);
    }
    return next;
  }, [list, sort]);

  const spotlight = sorted.slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200/70 bg-background-100/60">
          <div className="w-full px-4 md:px-8 lg:px-12 pt-8 pb-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-400">热门推荐</span>
            </nav>

            <div className="mt-5">
              <p className="text-[11px] tracking-[0.3em] text-accent-500 mb-2">POPULAR RANKING</p>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide">热门推荐榜单</h1>
              <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-2xl">
                榜单依据站内浏览量、收藏量与更新活跃度模拟生成，仅代表本站演示数据，不代表作品实际质量排名。
              </p>
            </div>

            <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full bg-background-200/70">
              {tabOptions.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setSort(tab.key)}
                  className={`h-8 px-4 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === tab.key ? "bg-primary-600 text-foreground-950" : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-12">
          <SectionHeader
            eyebrow="TOP 3"
            title="本期编辑精选"
            description="综合热度与口碑选出的前三部作品，适合作为了解本站内容风格的入口。"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {spotlight.map((item, index) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group relative rounded-lg overflow-hidden border border-background-300/50 bg-background-100 aspect-[16/10]"
              >
                <img
                  src={item.banner}
                  alt={`${item.title} 主视觉`}
                  title={`${item.title} ${item.category} ${item.year}`}
                  className="absolute inset-0 w-full h-full object-cover object-top opacity-70 group-hover:opacity-85 transition-opacity duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-label text-2xl text-accent-500 leading-none">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="inline-flex items-center h-5 px-1.5 rounded bg-background-50/70 backdrop-blur-sm text-[10px] text-accent-400 border border-accent-600/30">
                      {item.category}
                    </span>
                    <span className="text-[11px] text-foreground-600">{formatViews(item.views)} 浏览</span>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 group-hover:text-accent-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">{item.synopsis}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12">
            <SectionHeader
              eyebrow="FULL LIST"
              title="完整热门列表"
              description="按你选择的排序方式展示，点击卡片查看作品详情与同类型推荐。"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-7">
              {sorted.slice(0, visible).map((item, index) => (
                <AnimeCard key={item.id} anime={item} showRank={sort === "popular" ? index + 1 : undefined} />
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              <p className="text-xs text-foreground-500">已显示 {Math.min(visible, sorted.length)} / {sorted.length} 部</p>
              {visible < sorted.length ? (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="h-11 px-8 rounded-md border border-accent-600/50 text-accent-400 hover:bg-accent-500/10 text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多
                  <i className="ri-arrow-down-line" />
                </button>
              ) : (
                <span className="text-xs text-foreground-400">已经到底了</span>
              )}
            </div>
          </div>

          <div className="mt-12 rounded-lg border border-background-300/50 bg-background-100/60 p-5 md:p-6">
            <h2 className="text-sm text-foreground-900 tracking-wide mb-3 flex items-center gap-2">
              <i className="ri-line-chart-line text-accent-500" />
              榜单数据说明
            </h2>
            <p className="text-xs text-foreground-500 leading-relaxed max-w-3xl">
              热度值由浏览量、收藏量与更新时间共同加权计算，用于站内排序展示，每 7 天刷新一次。
              榜单数据为演示用途，最新更新日期为 {formatDate(sorted[0]?.updatedAt || "")}。
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}